import React, { useState, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';

// Styles
import './styles/tokens.css';
import './styles/glass.css';
import './styles/animations.css';
import './styles/typography.css';

// Lib
import { T } from './lib/tokens';
import { useSupabaseData } from './lib/hooks/useSupabaseData';
import { useKeyboard } from './lib/hooks/useKeyboard';
import { useTimeOfDay } from './lib/hooks/useTimeOfDay';
import { computePriorityStack, computeVentureBalance } from './lib/priorityEngine';
import { autoScheduleTasks } from './lib/scheduler';

// Layout
import { Sidebar } from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import { CommandPalette } from './components/layout/CommandPalette';
import { QuickCapture, QuickCaptureButton } from './components/layout/QuickCapture';
import { FocusMode } from './components/layout/FocusMode';
import { ToastProvider } from './components/layout/Toast';
import { ChatDock } from './components/layout/ChatDock';

// Views
import { TodayView } from './views/TodayView';
import { GoalsView } from './views/GoalsView';
import { InboxView } from './views/InboxView';
import { TasksView } from './views/TasksView';
import { AgentsView } from './views/AgentsView';
import { ResearchView } from './views/ResearchView';
import { MentoringView } from './views/MentoringView';
import { ProjectsView } from './views/ProjectsView';
import { ProjectDetailView } from './views/ProjectDetailView';
import { CalendarView } from './views/CalendarView';
import { DashboardView } from './views/DashboardView';
import { GanttView } from './views/GanttView';
import { SettingsView } from './views/SettingsView';
import { TimeTrackingView } from './views/TimeTrackingView';
import { PromisesView } from './views/PromisesView';
import { BriefsView } from './views/BriefsView';
import { AssistantView } from './views/AssistantView';
import { FinanceView } from './views/FinanceView';
import { AICommandBar } from './components/ai/AICommandBar';
import { LoginSplash } from './components/auth/LoginSplash';
import { ImportMeetingDialog } from './components/dialogs/ImportMeetingDialog';
import { AuthProvider, useAuth } from './lib/AuthContext';

export default function App() {
  return (
    <AuthProvider>
      <Root />
    </AuthProvider>
  );
}

function Root() {
  const { session, loading, signOut } = useAuth();

  // getSession() resolves almost immediately from localStorage.
  if (loading) {
    return <div style={{ minHeight: '100vh', background: T.bg }} />;
  }
  if (!session) {
    return <LoginSplash />;
  }
  return <AuthedApp onLogout={signOut} />;
}

function AuthedApp({ onLogout }) {
  const [view, setView] = useState('today');
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false);
  const [focusModeOpen, setFocusModeOpen] = useState(false);
  const [focusTask, setFocusTask] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [importMeetingOpen, setImportMeetingOpen] = useState(false);
  // Lets a view open the persona ChatDock (e.g. Money view -> "Chat with Penny").
  const [chatRequest, setChatRequest] = useState(null);

  // Data — only mounted once authed, so all the useSupabaseData fetches go out
  // with a valid x-exec-hq-key header.
  const data = useSupabaseData();
  const {
    goals, milestones, tasks, inbox, agents, agentLog,
    conflicts, routines, instructions, liveCalendar,
    founders, meetings, meetingActions,
    folders, scheduledBlocks, roles,
    promises, meetingBriefs,
    loading, load, triggerScheduler,
    markRead, markUnread, archiveItem, deleteItem, reorderItems,
  } = data;

  // Time of day
  const { config: timeConfig } = useTimeOfDay();

  // Venture balance for sidebar
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const calendarEntries = liveCalendar || [];
  const { scheduled } = autoScheduleTasks(calendarEntries, tasks.filter(t => !t.done), nowMin);
  const ventureBalance = computeVentureBalance(calendarEntries, scheduled);

  // Priority stack for focus mode
  const priorityStack = computePriorityStack({ tasks, goals, milestones, calendarEvents: calendarEntries, inbox });

  const unreadCount = inbox.filter(i => !i.is_read && !i.is_archived).length;

  // Background gradient based on time of day
  const bgVariant = timeConfig.bgVariant;
  const bgGradient = bgVariant === 'morning' ? T.bgMorning
    : bgVariant === 'evening' ? T.bgEvening
    : T.bg;

  // Actions dispatcher for command palette
  const handleAction = useCallback((action, payload) => {
    switch (action) {
      case 'quickCapture': setQuickCaptureOpen(true); break;
      case 'focusMode': {
        const topTask = priorityStack.find(p => p.type === 'task');
        setFocusTask(topTask?.item || null);
        setFocusModeOpen(true);
        break;
      }
      case 'refresh': load(); break;
      default: break;
    }
  }, [priorityStack, load]);

  // Global keyboard shortcuts
  useKeyboard({
    'command+k': () => setCmdPaletteOpen(true),
    'command+n': () => setQuickCaptureOpen(true),
    'command+j': () => setAiChatOpen(true),
    'escape': () => {
      setCmdPaletteOpen(false);
      setQuickCaptureOpen(false);
      setAiChatOpen(false);
    },
    'f': () => {
      if (!cmdPaletteOpen && !quickCaptureOpen) {
        const topTask = priorityStack.find(p => p.type === 'task');
        setFocusTask(topTask?.item || null);
        setFocusModeOpen(true);
      }
    },
    '1': () => { if (!cmdPaletteOpen && !quickCaptureOpen) setView('today'); },
    '2': () => { if (!cmdPaletteOpen && !quickCaptureOpen) { setSelectedProject(null); setView('projects'); } },
    '3': () => { if (!cmdPaletteOpen && !quickCaptureOpen) setView('calendar'); },
    '4': () => { if (!cmdPaletteOpen && !quickCaptureOpen) setView('tasks'); },
    '5': () => { if (!cmdPaletteOpen && !quickCaptureOpen) setView('goals'); },
    '6': () => { if (!cmdPaletteOpen && !quickCaptureOpen) setView('inbox'); },
    '7': () => { if (!cmdPaletteOpen && !quickCaptureOpen) setView('agents'); },
    '8': () => { if (!cmdPaletteOpen && !quickCaptureOpen) setView('research'); },
    '9': () => { if (!cmdPaletteOpen && !quickCaptureOpen) setView('mentoring'); },
    '0': () => { if (!cmdPaletteOpen && !quickCaptureOpen) setView('finance'); },
    'r': () => { if (!cmdPaletteOpen && !quickCaptureOpen) load(); },
  }, [cmdPaletteOpen, quickCaptureOpen, priorityStack]);

  // View rendering
  const views = {
    today: (
      <TodayView
        inbox={inbox} tasks={tasks} goals={goals} milestones={milestones}
        conflicts={conflicts} agentLog={agentLog} liveCalendar={liveCalendar}
        scheduledBlocks={scheduledBlocks}
        promises={promises} meetingBriefs={meetingBriefs}
        onUpdate={load} onNavigate={setView} triggerScheduler={triggerScheduler}
      />
    ),
    briefs: <BriefsView meetingBriefs={meetingBriefs} onUpdate={load} />,
    kay: <AssistantView persona="kay" />,
    saul: <AssistantView persona="saul" />,
    penny: <AssistantView persona="penny" />,
    finance: <FinanceView onChatWithPenny={() => setChatRequest({ slug: 'penny', ts: Date.now() })} />,
    promises: <PromisesView promises={promises} roles={roles} onUpdate={load} />,
    projects: selectedProject ? (
      <ProjectDetailView
        project={goals.find(g => g.id === selectedProject.id) || selectedProject}
        milestones={milestones} tasks={tasks}
        onBack={() => setSelectedProject(null)}
        onUpdate={load} triggerScheduler={triggerScheduler}
      />
    ) : (
      <ProjectsView
        goals={goals} milestones={milestones} tasks={tasks} roles={roles}
        onUpdate={load} onSelectProject={setSelectedProject}
      />
    ),
    calendar: (
      <CalendarView
        scheduledBlocks={scheduledBlocks} liveCalendar={liveCalendar}
        onUpdate={load} triggerScheduler={triggerScheduler}
      />
    ),
    goals: <GoalsView goals={goals} milestones={milestones} tasks={tasks} onUpdate={load} />,
    inbox: (
      <InboxView
        inbox={inbox}
        onMarkRead={markRead} onMarkUnread={markUnread}
        onArchive={archiveItem} onDelete={deleteItem} onReorder={reorderItems}
      />
    ),
    gantt: (
      <GanttView
        goals={goals} milestones={milestones} tasks={tasks}
        scheduledBlocks={scheduledBlocks} onUpdate={load}
      />
    ),
    tasks: <TasksView tasks={tasks} goals={goals} onUpdate={load} triggerScheduler={triggerScheduler} />,
    dashboard: (
      <DashboardView
        goals={goals} milestones={milestones} tasks={tasks}
        onUpdate={load} onSelectProject={(p) => { setSelectedProject(p); setView('projects'); }}
      />
    ),
    agents: (
      <AgentsView
        agents={agents} agentLog={agentLog} routines={routines}
        instructions={instructions} onUpdate={load}
      />
    ),
    timetrack: <TimeTrackingView goals={goals} tasks={tasks} />,
    settings: <SettingsView onUpdate={load} onLogout={onLogout} />,
    research: <ResearchView inbox={inbox} />,
    mentoring: (
      <MentoringView
        founders={founders} meetings={meetings} meetingActions={meetingActions}
        onUpdate={load}
      />
    ),
  };

  return (
    <ToastProvider>
      <div style={{
        height: '100vh', display: 'flex', flexDirection: 'column',
        background: bgGradient,
        backgroundSize: '400% 400%',
        animation: 'gradientDrift 60s ease infinite',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Ambient light orbs — give the canvas a "lit-from-behind" iCloud feel */}
        <div aria-hidden="true" style={{
          position: 'absolute', pointerEvents: 'none', zIndex: 0,
          top: '-20%', left: '-15%', width: '70vw', height: '70vw',
          background: 'radial-gradient(circle, rgba(212,146,30,0.22) 0%, rgba(212,146,30,0.08) 35%, transparent 65%)',
          filter: 'blur(40px)',
        }} />
        <div aria-hidden="true" style={{
          position: 'absolute', pointerEvents: 'none', zIndex: 0,
          bottom: '-25%', right: '-10%', width: '65vw', height: '65vw',
          background: 'radial-gradient(circle, rgba(139,114,200,0.28) 0%, rgba(139,114,200,0.10) 35%, transparent 65%)',
          filter: 'blur(40px)',
        }} />
        <div aria-hidden="true" style={{
          position: 'absolute', pointerEvents: 'none', zIndex: 0,
          top: '40%', right: '20%', width: '40vw', height: '40vw',
          background: 'radial-gradient(circle, rgba(74,158,204,0.18) 0%, rgba(74,158,204,0.05) 40%, transparent 70%)',
          filter: 'blur(60px)',
        }} />

        <div style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative', zIndex: 1 }}>
          <Sidebar
            view={view} onNav={setView} agents={agents}
            unreadCount={unreadCount} ventureBalance={ventureBalance}
          />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <TopBar
              onRefresh={load} loading={loading}
              onOpenCommandPalette={() => setCmdPaletteOpen(true)}
              onImportMeeting={() => setImportMeetingOpen(true)}
            />
            <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
              <AnimatePresence mode="wait">
                <div key={view}>
                  {views[view]}
                </div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Quick Capture FAB */}
        <QuickCaptureButton onClick={() => setQuickCaptureOpen(true)} />

        {/* Persona chat — floating Kay, Saul & Penny bubbles */}
        <ChatDock openRequest={chatRequest} />

        {/* Overlays */}
        <AnimatePresence>
          {cmdPaletteOpen && (
            <CommandPalette
              open={cmdPaletteOpen}
              onClose={() => setCmdPaletteOpen(false)}
              goals={goals} tasks={tasks} agents={agents}
              onNavigate={setView}
              onAction={handleAction}
            />
          )}
        </AnimatePresence>

        <QuickCapture
          open={quickCaptureOpen}
          onClose={() => setQuickCaptureOpen(false)}
          onUpdate={load}
        />

        <FocusMode
          open={focusModeOpen}
          task={focusTask}
          onClose={() => setFocusModeOpen(false)}
          onUpdate={load}
        />

        <AnimatePresence>
          {aiChatOpen && (
            <AICommandBar
              open={aiChatOpen}
              onClose={() => setAiChatOpen(false)}
              onUpdate={load}
              triggerScheduler={triggerScheduler}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {importMeetingOpen && (
            <ImportMeetingDialog
              founders={founders}
              onClose={() => setImportMeetingOpen(false)}
              onImported={load}
            />
          )}
        </AnimatePresence>
      </div>
    </ToastProvider>
  );
}
